import { useState, useEffect } from 'react';
import { albumsApi } from '@/services/api/albums';
import { imageBlobCache } from '@/services/cache/imageBlobCache';
import { downscaleImageBlob } from '@/utils/downscaleImage';

interface UseAlbumCoverImageResult {
  imageUrl: string | null;
  isLoading: boolean;
  hasError: boolean;
}

// De-dupe concurrent fetches for the same album cover (keyed by cache key).
const inFlightRequests = new Map<string, Promise<string>>();

/**
 * Fetch and cache an album's cover art. Persists a downscaled blob to
 * IndexedDB (via imageBlobCache) so it is downloaded once and served instantly
 * on every subsequent load — mirrors the song cover behaviour.
 *
 * `cacheToken` (derived from the cover URL) busts the persistent cache when the
 * album's artwork changes, so a desktop edit shows up immediately.
 */
export function useAlbumCoverImage(
  albumId: number,
  shouldFetch: boolean = true,
  cacheToken?: string
): UseAlbumCoverImageResult {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(shouldFetch);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    if (!shouldFetch) {
      setIsLoading(false);
      setImageUrl(null);
      setHasError(false);
      return;
    }

    let mounted = true;
    setIsLoading(true);
    setHasError(false);

    async function fetchImage() {
      const dedupeKey = imageBlobCache.getAlbumCacheKey(albumId, cacheToken);
      try {
        // Persistent cache first.
        const cachedUrl = await imageBlobCache.getAlbum(albumId, cacheToken);
        if (cachedUrl) {
          if (mounted) {
            setImageUrl(cachedUrl);
            setIsLoading(false);
          }
          return;
        }

        let requestPromise = inFlightRequests.get(dedupeKey);
        if (!requestPromise) {
          requestPromise = (async () => {
            const response = await albumsApi.getCoverArtUrl(albumId, { expiration: 3600 });
            const imageResponse = await fetch(response.coverArtUrl);
            if (!imageResponse.ok) {
              throw new Error(`Failed to fetch album cover: ${imageResponse.status}`);
            }
            const original = await imageResponse.blob();
            const blob = await downscaleImageBlob(original);
            // Cache for 1 week (same as song covers).
            const objectUrl = await imageBlobCache.setAlbum(albumId, blob, 168, cacheToken);
            inFlightRequests.delete(dedupeKey);
            return objectUrl;
          })().catch((error) => {
            inFlightRequests.delete(dedupeKey);
            throw error;
          });
          inFlightRequests.set(dedupeKey, requestPromise);
        }

        const url = await requestPromise;
        if (mounted) {
          setImageUrl(url);
          setIsLoading(false);
        }
      } catch {
        if (mounted) {
          setHasError(true);
          setIsLoading(false);
        }
      }
    }

    fetchImage();

    return () => {
      mounted = false;
    };
  }, [albumId, shouldFetch, cacheToken]);

  return { imageUrl, isLoading, hasError };
}
